/**
 * Constrói o resource Schedule FHIR R5 específico do Pronto Atendimento.
 * Inclui Location + HealthcareService (1) + Practitioner.
 * Não altera auxiliarData.Schedule (agenda normal permanece intacta).
 */
import dayjs from "dayjs";
import {
  PRONTO_ATENDIMENTO_DEFAULT_EXTENSION_URL,
  PRONTO_ATENDIMENTO_SERVICE_CATEGORY,
  SCHEDULE_ATTENDANCE_TYPE_EXTENSION_URL,
  SERVICE_COST_EXTENSION_URL,
} from "./serviceCategory";
import type { ProntoAtendimentoMode } from "./types";

/** Mapeia modo da UI para valueCode das extensions Fluxmed. */
export function modeToAttendanceType(
  mode: ProntoAtendimentoMode,
): "presencial" | "teleatendimento" | "chat" {
  if (mode === "remoto") return "teleatendimento";
  return mode;
}

export function attendanceTypeToMode(
  valueCode?: string,
): ProntoAtendimentoMode {
  if (valueCode === "teleatendimento") return "remoto";
  if (valueCode === "chat") return "chat";
  return "presencial";
}

export interface BuildProntoAtendimentoScheduleParams {
  id: string;
  name: string;
  mode: ProntoAtendimentoMode;
  value: number;
  isDefault: boolean;
  locationId: string;
  locationDisplay: string;
  practitionerRef: string;
  practitionerDisplay: string;
  /** Único HCS do PA (obrigatório na criação atual). */
  healthcareServiceId: string;
  healthcareServiceDisplay: string;
}

/**
 * Defaults sensatos: horizonte longo e duração de slot 30min.
 * Disponibilidade do dia fica no HealthcareService (availableTime).
 */
export function buildProntoAtendimentoSchedule(
  params: BuildProntoAtendimentoScheduleParams,
): Record<string, unknown> {
  const attendanceType = modeToAttendanceType(params.mode);
  const start = dayjs().startOf("day").format("YYYY-MM-DD");
  const end = dayjs().add(2, "year").endOf("day").format("YYYY-MM-DD");

  return {
    id: params.id,
    resourceType: "Schedule",
    active: true,
    name: params.name,
    serviceCategory: [
      {
        coding: [
          {
            system: PRONTO_ATENDIMENTO_SERVICE_CATEGORY.system,
            code: PRONTO_ATENDIMENTO_SERVICE_CATEGORY.code,
            display: PRONTO_ATENDIMENTO_SERVICE_CATEGORY.display,
          },
        ],
        text: PRONTO_ATENDIMENTO_SERVICE_CATEGORY.display,
      },
    ],
    // Mesma ordem da agenda normal: Location → HCS → Practitioner
    actor: [
      {
        reference: `Location/${params.locationId}`,
        display: params.locationDisplay,
      },
      {
        reference: `HealthcareService/${params.healthcareServiceId}`,
        display: params.healthcareServiceDisplay,
      },
      {
        reference: params.practitionerRef,
        display: params.practitionerDisplay,
      },
    ],
    planningHorizon: {
      start: `${start}T00:00:00.000`,
      end: `${end}T23:59:59.999`,
    },
    extension: [
      {
        url: "https://fluxmed.com.br/fhir/StructureDefinition/schedule-slot-duration",
        valueDuration: {
          value: "30",
          unit: "minutes",
          system: "http://unitsofmeasure.org",
          code: "min",
        },
      },
      {
        url: SERVICE_COST_EXTENSION_URL,
        valueDecimal: params.value,
      },
      {
        url: SCHEDULE_ATTENDANCE_TYPE_EXTENSION_URL,
        valueCode: attendanceType,
      },
      {
        url: PRONTO_ATENDIMENTO_DEFAULT_EXTENSION_URL,
        valueBoolean: params.isDefault,
      },
    ],
  };
}
